import React from 'react';
import { Link } from 'react-router-dom';
import SiteLayout, { PALETTE } from './SiteLayout';
import SEO from '../../components/SEO';

const FEATURES = [
  {
    eyebrow: '01, Operations',
    title: 'A dashboard that runs your day.',
    sub: 'Live counts of what dispatches, what’s on hold, what’s overdue.',
    bullets: [
      'Due today · Completed · Overdue · Ready-to-ship',
      'Weekly cases, fulfilment rate, batch flow',
      'QC pass rate + on-hold counts, live',
      '5-step daily cadence, no Friday scramble',
    ],
    img: '/product/dashboard.png',
    align: 'right',
    accent: PALETTE.magenta,
  },
  {
    eyebrow: '02, Sales Order Entry',
    title: 'Order entry your team will actually use.',
    sub: 'For the person taking the Tesco call at 11am with a forklift running outside.',
    bullets: [
      'Your SKU ↔ retailer SKU, auto-mapped',
      'Repeat-order memory, fewer keystrokes',
      'Real-time batch-availability check',
      'PO, delivery window, transport, captured on entry',
    ],
    img: '/product/order-entry.png',
    align: 'left',
    accent: PALETTE.gold,
  },
  {
    eyebrow: '03, Dispatch & Logistics',
    title: 'One screen from order to lorry.',
    sub: 'Every dispatch with status, weight, pallet count, filter, sort, export in seconds.',
    bullets: [
      'Live dispatch view with status colours',
      'CMR, pallet labels, dispatch notes generated together',
      'Customer-specific templates (Tesco / Aldi / Whole Foods)',
      'Backward-traceable to source batches',
    ],
    img: '/product/dispatches.png',
    align: 'right',
    accent: PALETTE.magenta,
  },
  {
    eyebrow: '04, Documents',
    title: 'BRC-grade paperwork in 4 seconds.',
    sub: 'Dispatch notes, COAs, pallet labels, branded, consistent, audit-ready.',
    bullets: [
      'PDF dispatch note with traceability footer',
      'COA auto-built from QC sample data',
      'GS1 pallet labels · retailer-specific layouts',
      'One-click audit pack export',
    ],
    img: '/product/documents.png',
    align: 'left',
    accent: PALETTE.gold,
  },
  {
    eyebrow: '05, Backward Traceability',
    title: 'Batch to supplier lot, in seconds.',
    sub: '60-minute traceability ready for BRCGS §3.9, IFS and every national food authority.',
    bullets: [
      'Customer → dispatch → batch → make day → silo → supplier',
      'Ingredient + packaging lots resolved alongside milk',
      'Forward trace runs just as fast',
      'Printable audit evidence for BRCGS, IFS, FSAI, NÉBIH',
    ],
    img: '/product/compliance.png',
    align: 'right',
    accent: PALETTE.magenta,
  },
  {
    eyebrow: '06, Traceability Events',
    title: 'A live audit log of every recall drill.',
    sub: 'Every saved mock recall becomes an event with reference, workflow, notes and a PDF response pack.',
    bullets: [
      'Draft → review → approved → comms → closed',
      'Tamper-evident: every change stamped',
      'Server-side PDF response pack',
      'Re-open any event to continue investigating',
    ],
    img: '/product/compliance-report.png',
    align: 'left',
    accent: PALETTE.gold,
  },
  {
    eyebrow: '07, Milk Intake & Silos',
    title: 'Every litre, accounted for from the gate.',
    sub: 'Weighbridge to silo to make day, every drop traceable.',
    bullets: [
      'Volume, supplier, silo, antibiotic, fat, protein, temp',
      'Antibiotic screen at intake, before the silo',
      'Silo ledger: intake → silo → make day, linked',
      'Trace stops at the supplier lot, not a notebook',
    ],
    img: '/product/inwards-milk-intake.png',
    align: 'right',
    accent: PALETTE.magenta,
  },
  {
    eyebrow: '08, Mock Recall',
    title: 'A drill in 4 seconds. A real recall in 4 minutes.',
    sub: 'Any starting point, supplier lot, milk intake, make day or batch. Downstream footprint on screen.',
    bullets: [
      'Make days · batches · dispatches · customers · quantity',
      'Customers by name + city, not opaque codes',
      'Customer draft comms generated (never auto-sent)',
      'Save as an event for audit + re-open later',
    ],
    img: '/product/mock-recall.png',
    align: 'left',
    accent: PALETTE.gold,
  },
  {
    eyebrow: '09, Adaptive Intelligence',
    title: 'It learns your factory. Your people stay in control.',
    sub: 'Your factory should not have to adapt to software. Keystone adapts to your factory.',
    bullets: [
      'Customer-specific quantities, weights and units learned from real use',
      'Recurring workflow patterns applied automatically, no formula to maintain',
      'Every suggestion is overridable, the operator always decides',
      'Teach it once, it shouldn’t need teaching twice',
    ],
    img: '/product/order-entry.png',
    align: 'right',
    accent: PALETTE.magenta,
  },
];

const TINY_FEATURES = [
  { t: 'Batch traceability', d: '60-min forward + backward trace, BRCGS / IFS ready.' },
  { t: 'QC management', d: 'Schedule samples. Track deviations. Hold-and-release enforced.' },
  { t: 'Production planner', d: 'Drag-and-drop weekly grid. Capacity at a glance.' },
  { t: 'Customer code mapping', d: 'Your SKUs ↔ retailer codes, automatic.' },
  { t: 'Pallet label designer', d: 'GS1-128 · retailer layouts · drag-and-drop.' },
  { t: 'Backup & restore', d: 'Encrypted nightly. Point-in-time restore.' },
  { t: 'Audit log', d: 'Every change, every user, before/after, immutable.' },
  { t: 'Compliance Centre', d: 'BRCGS / IFS Food traceability, IFS Food, EU food safety, baked in.' },
  { t: 'Co-branding', d: 'Distributor / own-label templates, no re-keying.' },
];

export default function SiteFeatures() {
  return (
    <SiteLayout>
      <SEO
        title="Keystone Features, Traceability, Dispatch & Planner"
        description="Every Keystone module: weekly planner, batch traceability, dispatch notes, automated COAs, retailer pallet labels and customer SKU mapping. Licensed as one product, sized to your operation."
        path="/site/features"
        keywords="dispatch software, batch traceability, pallet labels Aldi Tesco Lidl, COA generator, weekly production planner, customer SKU mapping, food ERP features, FSAI HACCP software"
      />
      {/* HERO */}
      <section data-testid="site-features-hero" style={{
        background: PALETTE.cream,
        padding: '160px 32px 60px',
        borderBottom: `1px solid ${PALETTE.border}`,
      }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 20 }}>
            Features
          </div>
          <h1 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(40px, 5.4vw, 72px)',
            fontWeight: 700, lineHeight: 1, letterSpacing: '-0.03em', color: PALETTE.navy, margin: 0, maxWidth: 980,
          }}>
            Twelve modules.<br/>
            <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>One spine.</em>
          </h1>
          <p style={{ fontSize: 20, lineHeight: 1.55, color: PALETTE.slate, marginTop: 24, maxWidth: 660, fontWeight: 400 }}>
            Every module replaces a folder of spreadsheets or one person’s memory. They share one batch register, trace queries collapse from hours to seconds.
          </p>
        </div>
      </section>

      {/* MAIN FEATURES */}
      {FEATURES.map((f, i) => (
        <section key={f.title} style={{
          background: i % 2 === 0 ? PALETTE.paper : PALETTE.cream,
          padding: '110px 32px',
          borderBottom: `1px solid ${PALETTE.border}`,
        }}>
          <div style={{
            maxWidth: 1280,
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: f.align === 'left' ? '1fr 1.15fr' : '1.15fr 1fr',
            gap: 80,
            alignItems: 'center',
          }} className="feat-row">
            {/* Image */}
            <div style={{
              order: f.align === 'left' ? 1 : 2,
              position: 'relative',
            }} className={f.align === 'left' ? 'feat-img-left' : 'feat-img-right'}>
              <div style={{
                position: 'relative',
                borderRadius: 12,
                overflow: 'hidden',
                boxShadow: '0 30px 60px -15px rgba(10,37,64,0.30), 0 8px 20px -6px rgba(10,37,64,0.18)',
                border: `1px solid ${PALETTE.border}`,
                background: '#fff',
              }}>
                {/* Browser chrome */}
                <div style={{
                  background: '#F1ECE2',
                  borderBottom: `1px solid ${PALETTE.border}`,
                  padding: '9px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#FF5F57' }} />
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#FEBC2E' }} />
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#28C840' }} />
                </div>
                <img
                  src={f.img}
                  alt={f.title}
                  style={{ display: 'block', width: '100%', height: 'auto' }}
                />
              </div>

              {/* Floating accent number */}
              <div style={{
                position: 'absolute',
                top: -28,
                left: f.align === 'left' ? 'auto' : -16,
                right: f.align === 'left' ? -16 : 'auto',
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 'clamp(80px, 10vw, 140px)',
                fontWeight: 700,
                lineHeight: 1,
                color: f.accent,
                opacity: 0.12,
                pointerEvents: 'none',
                letterSpacing: '-0.05em',
                zIndex: -1,
              }}>{(i + 1).toString().padStart(2, '0')}</div>
            </div>

            {/* Copy */}
            <div style={{
              order: f.align === 'left' ? 2 : 1,
              maxWidth: 540,
            }} className={f.align === 'left' ? 'feat-copy-right' : 'feat-copy-left'}>
              <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: f.accent, marginBottom: 14 }}>
                {f.eyebrow}
              </div>
              <h2 style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 'clamp(30px, 4vw, 46px)',
                fontWeight: 700,
                color: PALETTE.navy,
                margin: 0,
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
              }}>
                {f.title}
              </h2>
              <p style={{ fontSize: 18, lineHeight: 1.6, color: PALETTE.slate, marginTop: 20 }} dangerouslySetInnerHTML={{ __html: f.sub }} />
              <ul style={{ padding: 0, margin: '28px 0 0', listStyle: 'none' }}>
                {f.bullets.map((b, j) => (
                  <li key={j} style={{
                    display: 'flex',
                    gap: 14,
                    padding: '10px 0',
                    fontSize: 15,
                    lineHeight: 1.55,
                    color: PALETTE.ink,
                    borderTop: j === 0 ? `1px solid ${PALETTE.border}` : 'none',
                    borderBottom: `1px solid ${PALETTE.border}`,
                  }}>
                    <span style={{
                      flexShrink: 0,
                      marginTop: 6,
                      width: 6, height: 6,
                      borderRadius: '50%',
                      background: f.accent,
                    }} />
                    <span dangerouslySetInnerHTML={{ __html: b }} />
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <style>{`
            @media (max-width: 1000px) {
              .feat-row { grid-template-columns: 1fr !important; gap: 48px !important; }
              .feat-img-left, .feat-img-right { order: 1 !important; }
              .feat-copy-left, .feat-copy-right { order: 2 !important; }
            }
          `}</style>
        </section>
      ))}

      {/* TINY FEATURES GRID */}
      <section style={{ background: PALETTE.paper, padding: '110px 32px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 64, alignItems: 'baseline', marginBottom: 56 }} className="more-head">
            <div>
              <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 14 }}>
                And the rest of the spine
              </div>
              <h2 style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 'clamp(32px, 4.4vw, 50px)',
                fontWeight: 700,
                color: PALETTE.navy,
                margin: 0,
                letterSpacing: '-0.02em',
                lineHeight: 1.05,
              }}>
                Modules you'll<br/>
                <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>discover on Tuesday.</em>
              </h2>
            </div>
            <p style={{ fontSize: 16, lineHeight: 1.55, color: PALETTE.slate, margin: 0 }}>
              The features above carry the day. These run quietly underneath, until the auditor asks something specific.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 0,
            border: `1px solid ${PALETTE.border}`,
          }} className="more-grid">
            {TINY_FEATURES.map((tf, i) => (
              <div key={tf.t} style={{
                padding: 32,
                borderRight: (i + 1) % 3 === 0 ? 'none' : `1px solid ${PALETTE.border}`,
                borderBottom: i < TINY_FEATURES.length - 3 ? `1px solid ${PALETTE.border}` : 'none',
                background: PALETTE.paper,
                transition: 'background 0.2s',
              }}
                onMouseEnter={e => e.currentTarget.style.background = PALETTE.bgSoft}
                onMouseLeave={e => e.currentTarget.style.background = PALETTE.paper}
              >
                <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 13, color: PALETTE.magenta, fontWeight: 700, letterSpacing: '0.1em' }}>
                  {(i + 7).toString().padStart(2, '0')}
                </div>
                <h3 style={{ fontSize: 20, fontWeight: 700, color: PALETTE.navy, margin: '12px 0 10px', letterSpacing: '-0.01em' }}>
                  {tf.t}
                </h3>
                <p style={{ fontSize: 14, lineHeight: 1.6, color: PALETTE.slate, margin: 0 }} dangerouslySetInnerHTML={{ __html: tf.d }} />
              </div>
            ))}
          </div>
        </div>
        <style>{`
          @media (max-width: 1000px) { .more-head { grid-template-columns: 1fr !important; gap: 32px !important; } }
          @media (max-width: 800px) { .more-grid { grid-template-columns: 1fr 1fr !important; } .more-grid > div { border-right: none !important; border-bottom: 1px solid ${PALETTE.border} !important; } }
          @media (max-width: 500px) { .more-grid { grid-template-columns: 1fr !important; } }
        `}</style>
      </section>

      {/* HOSTING & ISOLATION BAND */}
      <section style={{ background: PALETTE.navy, color: '#fff', padding: '90px 32px', position: 'relative', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: `radial-gradient(circle at 15% 30%, ${PALETTE.gold}11 0%, transparent 45%), radial-gradient(circle at 85% 70%, ${PALETTE.magenta}12 0%, transparent 45%)`,
          pointerEvents: 'none',
        }} />
        <div style={{ maxWidth: 1080, margin: '0 auto', position: 'relative' }}>
          <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.gold, marginBottom: 20, textAlign: 'center' }}>
            Hosting & Isolation
          </div>
          <h2 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(30px, 4vw, 46px)',
            fontWeight: 700, margin: 0, letterSpacing: '-0.02em', lineHeight: 1.1,
            textAlign: 'center', maxWidth: 880, marginLeft: 'auto', marginRight: 'auto',
          }}>
            One tenant. One server.<br/>
            <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.gold }}>Zero co-mingling.</em>
          </h2>
          <p style={{ fontSize: 16, lineHeight: 1.6, color: '#CBD5E1', marginTop: 20, maxWidth: 620, marginLeft: 'auto', marginRight: 'auto', textAlign: 'center' }}>
            Our managed AWS EU (Dublin, eu-west-1), or deploy inside your own AWS account. Dedicated server, dedicated database. No noisy neighbours.
          </p>
          <div style={{
            marginTop: 40, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24, maxWidth: 920, marginLeft: 'auto', marginRight: 'auto',
          }} className="iso-feat-grid">
            {[
              { t: 'Our cloud', d: 'Managed AWS eu-west-1 · daily encrypted backups · 99.95% SLA' },
              { t: 'Your cloud', d: 'IaC templates for AWS · deploy in your VPC · we keep the keys, you keep the data' },
              { t: 'Hybrid', d: 'Production data on your AWS · audit logs replicated to managed cluster' },
            ].map(c => (
              <div key={c.t} style={{ padding: 24, border: '1px solid rgba(255,255,255,0.10)', borderRadius: 4 }}>
                <div style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.gold, marginBottom: 10 }}>{c.t}</div>
                <div style={{ fontSize: 14, lineHeight: 1.55, color: '#CBD5E1' }}>{c.d}</div>
              </div>
            ))}
          </div>
        </div>
        <style>{`
          @media (max-width: 800px) { .iso-feat-grid { grid-template-columns: 1fr !important; } }
        `}</style>
      </section>

      {/* CTA */}
      <section style={{ background: PALETTE.navy, color: '#fff', padding: '100px 32px', textAlign: 'center' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.gold, marginBottom: 18 }}>
            See it on your data
          </div>
          <h2 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(32px, 4.4vw, 48px)',
            fontWeight: 700,
            margin: 0,
            letterSpacing: '-0.02em',
            lineHeight: 1.05,
          }}>
            Twenty minutes.<br/>
            <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.gold }}>Your batch numbers, our screen.</em>
          </h2>
          <p style={{ fontSize: 17, lineHeight: 1.55, color: '#CBD5E1', marginTop: 20, maxWidth: 540, marginLeft: 'auto', marginRight: 'auto' }}>
            Bring one painful trace query. We’ll run it in Keystone. Your data, our screen.
          </p>
          <Link to="/site/contact" data-testid="features-cta" style={{
            display: 'inline-block', marginTop: 36, padding: '16px 32px', background: PALETTE.gold, color: PALETTE.navy,
            textDecoration: 'none', fontSize: 14, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', borderRadius: 4,
          }}>
            Book a 20-min walkthrough →
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}
